import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { useSession } from '@/hooks/useSession';
import { getMaxBridge } from '@/lib/max-bridge';
import logoIcon from '@/assets/logo/logo-icon.png';

/**
 * Экран 8 — Загрузка результата.
 *
 * Источники истины:
 *  - UX-спека v1.2 «Экран 8» (анимация + сменяющиеся подсказки).
 *  - UX-спека v1.1 §4.3: создание лида в alfaCRM — фоновый процесс,
 *    не блокирующий показ результата.
 *
 * Поведение:
 *  - Минимум 2 сек (UX-спека). Максимума нет — пользователь НЕ должен
 *    попадать на /error из-за технического timeout (Phase 2.6 fix).
 *  - Подсказки сменяются каждые 800 мс циклически.
 *  - Фоновый промис alfaCRM (Phase 6) сейчас имитируется setTimeout
 *    с random(2000, 4000) мс. На reject — console.error, всё равно идём
 *    на /result (Phase 6 поднимет retry-очередь).
 *  - При переходе на /result — disableClosingConfirmation()
 *    (UX-спека 5.3: перед результатом снимаем guard).
 */

const HINTS = [
  'Считаем уровень цифровых навыков',
  'Анализируем сильные стороны',
  'Подбираем подходящие направления',
];

const HINT_INTERVAL_MS = 800;
const MIN_DURATION_MS = 2000;

/** Имитация alfaCRM (Phase 6 заменит на реальный POST). */
function simulateCrmLead(): Promise<void> {
  return new Promise((resolve, reject) => {
    const delay = MIN_DURATION_MS + Math.random() * (4000 - MIN_DURATION_MS);
    setTimeout(() => {
      // 95% шанс успеха в имитации; в реальности retry-очередь поднимет Phase 6.
      if (Math.random() < 0.95) resolve();
      else reject(new Error('mock_crm_unavailable'));
    }, delay);
  });
}

export default function Loading() {
  const navigate = useNavigate();
  const { state } = useSession();
  const [hintIdx, setHintIdx] = useState(0);

  // Звательный оборот через запятую: «Михаил, готовим твой цифровой профиль…».
  // Звательный = именительный для русских имён, поэтому не нужен морфологический
  // transform под падеж (Михаил→Михаила, Анна→Анны, Илья→Ильи и т.д.).
  // Если имени нет (defensive — пройти Loading без child маловероятно) — фолбэк
  // без запятой и без имени, без обращения.
  const childName = state.child?.name ?? '';
  const startedAtRef = useRef(Date.now());

  // Циклические подсказки.
  useEffect(() => {
    const id = window.setInterval(() => {
      setHintIdx((i) => (i + 1) % HINTS.length);
    }, HINT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Основной flow: ждём CRM, но соблюдаем min/max границы.
  useEffect(() => {
    let cancelled = false;

    function goResult() {
      if (cancelled) return;
      try {
        getMaxBridge().disableClosingConfirmation();
      } catch {
        // ignore
      }
      navigate('/result');
    }

    simulateCrmLead()
      .catch((err) => {
        // Лид не создался — не блокируем UX (UX-спека v1.1 §4.3).
        // TODO Phase 6: сюда попадает CRM 4xx/5xx — кладём в очередь retry,
        // событие crm_retry_attempted (UX-спека раздел 8). Пока — лог.
        console.error('[CRM mock] lead creation failed', err);
      })
      .finally(() => {
        if (cancelled) return;
        const elapsed = Date.now() - startedAtRef.current;
        const remainder = Math.max(0, MIN_DURATION_MS - elapsed);
        // Ждём оставшееся время до MIN_DURATION, потом уводим на /result.
        // Hard cap → /error удалён: технический timeout не должен показывать
        // родителю экран ошибки. Если CRM-промис никогда не resolve (что в
        // моке невозможно, в Phase 6 — дело backend reliability), пользователь
        // увидит spinner, а не /error-ловушку.
        window.setTimeout(goResult, remainder);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <ScreenLayout>
      <div className="flex flex-1 flex-col items-center justify-center gap-6 py-12">
        {/* Брендированный кольцевой spinner — вращающаяся оранжевая дуга
            вокруг неподвижного логотипа «IT» в центре. */}
        <div
          aria-label="Загрузка"
          role="status"
          className="relative h-24 w-24"
        >
          <span
            className="absolute inset-0 inline-block animate-spin rounded-full border-4 border-border border-t-accent"
          />
          <img
            src={logoIcon}
            alt=""
            aria-hidden="true"
            className="absolute inset-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] object-contain select-none"
            draggable={false}
          />
        </div>
        <div className="flex flex-col items-center gap-3 text-center">
          <h2 className="text-xl font-semibold text-text-primary">
            {childName ? `${childName}, готовим твой цифровой профиль…` : 'Готовим цифровой профиль…'}
          </h2>
          <p
            className="text-sm text-text-secondary transition-opacity duration-200"
            aria-live="polite"
          >
            {HINTS[hintIdx]}
          </p>
        </div>
      </div>
    </ScreenLayout>
  );
}
